import React, { useCallback, useEffect, useMemo, useState } from 'react';
import axios from 'axios';
import { Banner, Box, Button, ButtonGroup, Card, DataTable, Divider, InlineStack, Spinner, Text, TextField, BlockStack } from '@shopify/polaris';
import { CheckIcon, DeleteIcon, EditIcon, PlusIcon, XIcon } from '@shopify/polaris-icons';
import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import { useToast } from '#client/components/common/Toast/ToastContext';

const API_URL = import.meta.env.VITE_API_URL;

function normalizeDeviceIds(deviceIds) {
    const out = [];
    const seen = new Set();

    for (const raw of deviceIds || []) {
        const trimmed = String(raw ?? '').trim();
        if (!trimmed) continue;

        const key = trimmed.toUpperCase();
        if (seen.has(key)) continue;
        seen.add(key);
        out.push(trimmed);
    }

    return out;
}

function LabelPrinters() {
    const [deviceIdsByLocationId, setDeviceIdsByLocationId] = useState({});
    const [dirtyByLocationId, setDirtyByLocationId] = useState({});
    const [savingByLocationId, setSavingByLocationId] = useState({});
    const [editingByLocationId, setEditingByLocationId] = useState({});
    const { addToast, clearToasts } = useToast();

    const queryClient = useQueryClient();

    const locationsQuery = useQuery({
        queryKey: ['label-printers'],
        queryFn: async () => {
            const response = await axios.get(`${API_URL}/api/app/label-printers`, { withCredentials: true });
            return response?.data?.data?.locations ?? [];
        },
        staleTime: 5 * 60 * 1000, // 5 minutes
    });

    // Keep editable state in sync with fetched data, without clobbering dirty inputs.
    useEffect(() => {
        const locs = locationsQuery.data;
        if (!Array.isArray(locs)) return;

        setDeviceIdsByLocationId((prev) => {
            const next = { ...prev };
            for (const loc of locs) {
                const locationId = loc.id;
                if (dirtyByLocationId[locationId]) continue;
                next[locationId] = (loc.labelPrinters || []).map((p) => p.deviceId);
            }
            return next;
        });

        setEditingByLocationId((prev) => {
            const next = { ...prev };
            for (const loc of locs) {
                const locationId = loc.id;
                if (dirtyByLocationId[locationId]) continue;
                next[locationId] = null;
            }
            return next;
        });
    }, [locationsQuery.data, dirtyByLocationId]);

    const loadLocations = useCallback(async () => {
        await locationsQuery.refetch();
    }, [locationsQuery]);

    const sortedLocations = useMemo(() => {
        const locs = locationsQuery.data ?? [];
        return [...locs].sort((a, b) => (a?.name || '').localeCompare(b?.name || ''));
    }, [locationsQuery.data]);

    const saveLocationMutation = useMutation({
        mutationFn: async ({ locationId, deviceIds }) => {
            const response = await axios.put(`${API_URL}/api/app/label-printers/location/${locationId}`, { deviceIds }, { withCredentials: true });
            return response?.data;
        },
        onSuccess: (data, variables) => {
            console.log('Saved label printers for location', variables.locationId, data);

            if (!data.success) {
                clearToasts();
                addToast({ message: data.error.message, type: 'error' });
                return;
            }
            const updatedPrinters = data?.data?.labelPrinters ?? [];

            // Update draft state from server response and mark clean.
            setDeviceIdsByLocationId((prev) => ({ ...prev, [variables.locationId]: updatedPrinters.map((p) => p.deviceId) }));
            setDirtyByLocationId((prev) => ({ ...prev, [variables.locationId]: false }));
            setEditingByLocationId((prev) => ({ ...prev, [variables.locationId]: null }));

            // Update cached locations list too.
            queryClient.setQueryData(['label-printers'], (old) => {
                if (!Array.isArray(old)) return old;
                return old.map((loc) => {
                    if (loc.id !== variables.locationId) return loc;
                    return {
                        ...loc,
                        labelPrinters: updatedPrinters,
                    };
                });
            });
        },
    });

    const updateDeviceId = useCallback((locationId, index, value) => {
        setDeviceIdsByLocationId((prev) => {
            const next = { ...prev };
            const list = [...(next[locationId] || [])];
            list[index] = value;
            next[locationId] = list;
            return next;
        });

        setDirtyByLocationId((prev) => ({ ...prev, [locationId]: true }));
    }, []);

    const addDeviceIdRow = useCallback((locationId) => {
        setDeviceIdsByLocationId((prev) => {
            const next = { ...prev };
            const nextList = [...(next[locationId] || []), ''];
            next[locationId] = nextList;
            setEditingByLocationId((prevEditing) => ({ ...prevEditing, [locationId]: { index: nextList.length - 1, originalValue: '' } }));
            return next;
        });

        setDirtyByLocationId((prev) => ({ ...prev, [locationId]: true }));
    }, []);

    const removeDeviceIdRow = useCallback(
        async (locationId, index) => {
            try {
                setSavingByLocationId((prev) => ({ ...prev, [locationId]: true }));

                const currentDeviceIds = deviceIdsByLocationId[locationId] || [];
                const updatedDeviceIds = currentDeviceIds.filter((_, idx) => idx !== index);
                const normalizedDeviceIds = normalizeDeviceIds(updatedDeviceIds);

                await saveLocationMutation.mutateAsync({ locationId, deviceIds: normalizedDeviceIds });
            } catch (e) {
                // eslint-disable-next-line no-console
                console.error('Failed to delete label printer:', e);
                alert('Failed to delete label printer. Please try again.');
            } finally {
                setSavingByLocationId((prev) => ({ ...prev, [locationId]: false }));
            }
        },
        [deviceIdsByLocationId, saveLocationMutation]
    );

    const startEditDeviceIdRow = useCallback(
        (locationId, index) => {
            setEditingByLocationId((prev) => {
                const current = prev[locationId];
                if (current?.index === index) return prev;

                const currentValue = String((deviceIdsByLocationId[locationId] || [])[index] ?? '');
                return { ...prev, [locationId]: { index, originalValue: currentValue } };
            });
        },
        [deviceIdsByLocationId]
    );

    const cancelEditDeviceIdRow = useCallback((locationId) => {
        setEditingByLocationId((prev) => {
            const current = prev[locationId];
            if (!current) return prev;

            setDeviceIdsByLocationId((prevIds) => {
                const nextIds = { ...prevIds };
                const list = [...(nextIds[locationId] || [])];
                if (current.index >= 0 && current.index < list.length) {
                    list[current.index] = current.originalValue;
                }
                nextIds[locationId] = list;
                return nextIds;
            });

            return { ...prev, [locationId]: null };
        });
    }, []);

    const saveLocation = useCallback(
        async (locationId) => {
            try {
                setSavingByLocationId((prev) => ({ ...prev, [locationId]: true }));

                const deviceIds = normalizeDeviceIds(deviceIdsByLocationId[locationId] || []);
                await saveLocationMutation.mutateAsync({ locationId, deviceIds });
            } catch (e) {
                // eslint-disable-next-line no-console
                console.error('Failed to save label printers:', e);
                alert('Failed to save label printers. Please try again.');
            } finally {
                setSavingByLocationId((prev) => ({ ...prev, [locationId]: false }));
            }
        },
        [deviceIdsByLocationId, saveLocationMutation]
    );

    if (locationsQuery.isLoading) {
        return (
            <Card>
                <Box padding="400">
                    <InlineStack gap="300" align="start" blockAlign="center">
                        <Spinner size="small" />
                        <Text as="span">Loading label printers…</Text>
                    </InlineStack>
                </Box>
            </Card>
        );
    }

    const loadErrorMessage = locationsQuery.isError ? 'Failed to load label printers. Please refresh and try again.' : '';
    const saveErrorMessage = saveLocationMutation.isError ? 'Failed to save label printers. Please try again.' : '';
    const errorMessage = loadErrorMessage || saveErrorMessage;

    return (
        <BlockStack gap="400">
            <Box padding="400">
                <BlockStack gap="300">
                    <InlineStack align="space-between" blockAlign="center">
                        <Text variant="headingMd" as="h2">
                            Label Printers
                        </Text>
                        <Button onClick={loadLocations}>Refresh</Button>
                    </InlineStack>

                    <Banner tone="info">
                        <p>
                            Add one or more Zebra printers per Shopify location by entering the printer <b>Device ID</b>.
                        </p>
                        <p>Where to find it:</p>
                        <ul>
                            <li>
                                Find the device ID by logging into the Zebra developer portal at{' '}
                                <a href="https://developer.zebra.com/my-devices" target="_blank" rel="noreferrer">
                                    https://developer.zebra.com/my-devices
                                </a>
                                .
                            </li>
                            <li>It’s also commonly printed on the sticker on the bottom of the printer (often the serial number).</li>
                        </ul>
                    </Banner>

                    {errorMessage ? <Banner tone="critical">{errorMessage}</Banner> : null}
                </BlockStack>
            </Box>

            {sortedLocations.map((loc) => {
                const deviceIds = deviceIdsByLocationId[loc.id] || [];
                const saving = Boolean(savingByLocationId[loc.id]);
                const editing = editingByLocationId[loc.id];
                const editingIndex = editing?.index ?? null;

                return (
                    <Box padding="400" key={'location-' + loc.id}>
                        <BlockStack gap="300">
                            <InlineStack align="space-between" blockAlign="center">
                                <InlineStack gap="100">
                                    <Text variant="headingSm" as="h3">
                                        {loc.name}
                                    </Text>
                                    <Text tone="subdued" as="span">
                                        ({loc.locationGid})
                                    </Text>
                                </InlineStack>

                                <InlineStack gap="200" blockAlign="center">
                                    <Button icon={PlusIcon} onClick={() => addDeviceIdRow(loc.id)}>
                                        Add printer
                                    </Button>
                                </InlineStack>
                            </InlineStack>

                            <Divider />

                            {deviceIds.length === 0 ? (
                                <Text tone="subdued">No printers configured for this location.</Text>
                            ) : (
                                <DataTable
                                    columnContentTypes={['text', 'text']}
                                    headings={[
                                        'Device ID',
                                        <div key="actions-heading" style={{ textAlign: 'right' }}>
                                            Actions
                                        </div>,
                                    ]}
                                    rows={deviceIds.map((deviceId, idx) => [
                                        editingIndex === idx ? (
                                            <div
                                                onKeyDown={(e) => {
                                                    if (e.key === 'Enter') {
                                                        e.preventDefault();
                                                        saveLocation(loc.id);
                                                    }
                                                }}
                                            >
                                                <TextField
                                                    label="Device ID"
                                                    labelHidden
                                                    value={deviceId}
                                                    onChange={(value) => updateDeviceId(loc.id, idx, value)}
                                                    autoComplete="off"
                                                    placeholder="Enter printer Device ID"
                                                />
                                            </div>
                                        ) : (
                                            <Text as="span">{deviceId || '—'}</Text>
                                        ),
                                        <div key={`${loc.id}-${idx}-actions-cell`} style={{ display: 'flex', justifyContent: 'flex-end' }}>
                                            <ButtonGroup gap="tight">
                                                {editingIndex === idx ? (
                                                    <>
                                                        <Button
                                                            icon={XIcon}
                                                            onClick={() => cancelEditDeviceIdRow(loc.id)}
                                                            size="slim"
                                                            variant="plain"
                                                            accessibilityLabel={`Cancel edit device ID ${idx + 1}`}
                                                        />
                                                        <Button
                                                            icon={CheckIcon}
                                                            disabled={saving}
                                                            onClick={() => saveLocation(loc.id)}
                                                            size="slim"
                                                            variant="plain"
                                                            accessibilityLabel={`Save device ID ${idx + 1}`}
                                                        />
                                                    </>
                                                ) : (
                                                    <Button
                                                        icon={EditIcon}
                                                        onClick={() => startEditDeviceIdRow(loc.id, idx)}
                                                        size="slim"
                                                        variant="plain"
                                                        accessibilityLabel={`Edit device ID ${idx + 1}`}
                                                    />
                                                )}
                                                <Button
                                                    icon={DeleteIcon}
                                                    disabled={saving}
                                                    onClick={() => removeDeviceIdRow(loc.id, idx)}
                                                    size="slim"
                                                    variant="plain"
                                                    tone="critical"
                                                    accessibilityLabel={`Remove device ID ${idx + 1}`}
                                                />
                                            </ButtonGroup>
                                        </div>,
                                    ])}
                                />
                            )}
                        </BlockStack>
                    </Box>
                );
            })}
        </BlockStack>
    );
}

export default LabelPrinters;
